import React, { useState, useEffect } from "react";
import { useNotaire } from "../context/NotaireContext";
import { API_BASE } from "../config";
import { useMemberstack } from "@memberstack/react";
import TabsProfilFacture from "../components/TabsProfilFacture";

//catalyst tailwind
import { Heading } from '../components/catalyst/heading'
import { Input } from '../components/catalyst/input'
import { Field, Label } from '../components/catalyst/fieldset';
import { Divider } from '../components/catalyst/divider'
import classNames from 'classnames';
import { ChevronDownIcon, UserPlusIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/16/solid'
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from '@headlessui/react'
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const tabsNotaire = [
    { id: 'informations', label: 'Informations' },
    { id: 'equipe', label: 'Équipe' },
    { id: 'abonnement', label: 'Abonnement' },
    { id: 'mdp', label: 'Mot de passe' },
];

const tabsSecretaire = [
    { id: 'informations', label: 'Informations' },
    { id: 'mdp', label: 'Mot de passe' },
];

const MAX_COLLABORATEURS = 2;

function Profil(args) {
    const context = useNotaire();
    const isSecretaire = context.user?.role === 'secretaire';
    const tabs = isSecretaire ? tabsSecretaire : tabsNotaire;
    const [dataToSend, setDataToSend] = useState({});
    const [activeTab, setActiveTab] = useState(tabs[0].id);
    const [currentActiveTab, setCurrentActiveTab] = useState('informations');
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
    const memberstack = useMemberstack();

    // Équipe
    const [collaborateurs, setCollaborateurs] = useState([]);
    const [loadingCollab, setLoadingCollab] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [invitePrenom, setInvitePrenom] = useState("");
    const [inviteNom, setInviteNom] = useState("");
    const [inviteLoading, setInviteLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null); // { id, prenom, nom }

    const handleSelectChange = (e) => {
        const selectedId = tabs.find(tab => tab.label === e.target.value)?.id;
        if (selectedId) setActiveTab(selectedId);
    };

    // Chargement auto des collaborateurs quand l'onglet Équipe est actif
    useEffect(() => {
        if (activeTab === 'equipe') loadCollaborateurs();
    }, [activeTab]);

    const loadCollaborateurs = async () => {
        if (!context.infoEtude?.id) return;
        setLoadingCollab(true);
        try {
            const res = await fetch(`${API_BASE}/api/collaborateurs?etudeId=${context.infoEtude.id}`);
            const data = await res.json();
            setCollaborateurs(data.collaborateurs || []);
        } catch (e) {
            console.error("Erreur chargement collaborateurs", e);
        } finally {
            setLoadingCollab(false);
        }
    };

    const nonNotaires = collaborateurs.filter(c => c.role !== 'notaire');
    const limitAtteinte = nonNotaires.length >= MAX_COLLABORATEURS;

    const handleInviteCollaborateur = async (e) => {
        e.preventDefault();
        if (!inviteEmail || !invitePrenom || !inviteNom) return;
        setInviteLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/invite-collaborateur`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    etudeId: context.infoEtude.id,
                    email: inviteEmail,
                    prenom: invitePrenom,
                    nom: inviteNom,
                    notaireEmail: context.notaireEmail || context.user.email,
                    notaireAirtableId: context.member?.metaData?.airtable_id,
                }),
            });
            const data = await res.json();
            if (data.success) {
                toast.success(`Invitation envoyée à ${inviteEmail} !`);
                setInviteEmail("");
                setInvitePrenom("");
                setInviteNom("");
                setDrawerOpen(false);
                loadCollaborateurs();
            } else {
                toast.error(data.message || "Erreur lors de l'invitation.");
            }
        } catch (err) {
            toast.error("Erreur réseau");
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRemoveCollaborateur = async () => {
        if (!deleteModal) return;
        setDeletingId(deleteModal.id);
        try {
            const res = await fetch(`${API_BASE}/api/collaborateurs/${deleteModal.id}?etudeId=${context.infoEtude.id}`, {
                method: "DELETE",
            });
            const data = await res.json();
            if (data.success) {
                toast.success("Accès supprimé.");
                setDeleteModal(null);
                loadCollaborateurs();
            } else {
                toast.error(data.message || "Erreur lors de la suppression.");
            }
        } catch (err) {
            toast.error("Erreur réseau");
        } finally {
            setDeletingId(null);
        }
    };

    const modifUserInfo = async (event) => {
        event.preventDefault();
        const url = `${API_BASE}/api/etudes/${context.infoEtude.id}`;
        try {
            const response = await fetch(url, {
                method: "PATCH",
                headers: { Accept: "application/json", "Content-Type": "application/json" },
                body: JSON.stringify({ fields: dataToSend }),
            });
            if (response.ok) {
                toast.success("Modifications enregistrées !");
                setDataToSend({});
            } else {
                toast.error("Désolée, il y a eu une erreur, veuillez recommencer");
            }
        } catch (err) {
            toast.error("Erreur réseau");
        }
    };

    const updatePassword = async () => {
        if (newPassword !== newPasswordConfirm) {
            toast.error("Les mots de passe ne correspondent pas.");
            return;
        }
        try {
            await memberstack.updateMemberAuth({ oldPassword, newPassword });
            toast.success("Mot de passe modifié avec succès !");
            setOldPassword("");
            setNewPassword("");
            setNewPasswordConfirm("");
        } catch (e) {
            if (e.code === "invalid-password") {
                toast.error("Ancien mot de passe incorrect.");
            } else if (e.code === "invalid-password-to-short") {
                toast.error("Mot de passe trop court.");
            } else {
                toast.error("Erreur lors de la modification du mot de passe.");
            }
        }
    };

    return (
        <div className="container">
            <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
                <Heading>Votre profil notaire</Heading>
            </div>

            {/* Mobile (select) */}
            <div className="grid grid-cols-1 sm:hidden relative">
                <select
                    value={tabs.find((t) => t.id === activeTab)?.label}
                    onChange={handleSelectChange}
                    aria-label="Select a tab"
                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600"
                >
                    {tabs.map((tab) => (
                        <option key={tab.id}>{tab.label}</option>
                    ))}
                </select>
                <ChevronDownIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500" />
            </div>

            {/* Desktop (tabs) */}
            <div className="hidden sm:block">
                <div className="border-b border-gray-200">
                    <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                aria-current={activeTab === tab.id ? 'page' : undefined}
                                className={classNames(
                                    activeTab === tab.id
                                        ? 'border-indigo-500 text-indigo-600 font-bold'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                    'border-b-2 px-1 py-4 text-sm font-bold whitespace-nowrap'
                                )}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div>
                <div className="bg-slate-100">
                    <div className="pt-4 min-h-[100vh]">

                        {/* ── Informations ── */}
                        {activeTab === "informations" && (
                            <div className="overflow-hidden rounded-lg bg-white">
                                <div className="px-4 py-3 sm:p-6">
                                    <h3 className="text-base font-semibold leading-7 text-gray-900">Informations de votre étude</h3>
                                    <p className="mt-1 text-sm leading-6 text-gray-500">Veuillez nous contacter pour modifier votre email</p>
                                    <Divider className="mt-3" />
                                    <form onSubmit={modifUserInfo}>
                                        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>Email etude</Label>
                                                    <Input disabled defaultValue={context.infoEtude?.email || ""} type="text" />
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>Nom de l'étude</Label>
                                                    <Input name="nom" defaultValue={context.infoEtude?.nom || ""} onChange={(val) => setDataToSend(prev => ({ ...prev, nom: val.target.value }))} type="text" />
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>Adresse de l'étude</Label>
                                                    <Input defaultValue={context.infoEtude?.adresse || ""} onChange={(val) => setDataToSend(prev => ({ ...prev, adresse: val.target.value }))} type="text" />
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>Téléphone de l'étude</Label>
                                                    <Input defaultValue={context.infoEtude?.telephone || ""} onChange={(val) => setDataToSend(prev => ({ ...prev, telephone: val.target.value }))} type="text" />
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>Site web de l'étude</Label>
                                                    <Input defaultValue={context.infoEtude?.site || ""} onChange={(val) => setDataToSend(prev => ({ ...prev, site: val.target.value }))} type="text" />
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <Field>
                                                    <Label>SIRET de l'étude</Label>
                                                    <Input defaultValue={context.infoEtude?.siret || ""} onChange={(val) => setDataToSend(prev => ({ ...prev, siret: val.target.value }))} type="text" />
                                                </Field>
                                            </div>
                                            <button type="submit" className="btn-primary">Modifier</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* ── Équipe ── */}
                        {activeTab === "equipe" && (
                            <div className="overflow-hidden rounded-lg bg-white">
                                <div className="px-4 py-3 sm:p-6">
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className="text-base font-semibold leading-7 text-gray-900">Équipe</h3>
                                        {!limitAtteinte && nonNotaires.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setDrawerOpen(true)}
                                                className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                            >
                                                <UserPlusIcon className="size-4" />
                                                Ajouter un membre
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mb-4">
                                        Invitez vos secrétaires ou collaborateurs (max {MAX_COLLABORATEURS}).
                                    </p>
                                    <Divider className="mb-6" />

                                    {loadingCollab ? (
                                        <p className="text-sm text-gray-400 py-8 text-center">Chargement...</p>
                                    ) : nonNotaires.length === 0 ? (
                                        /* ── Empty state ── */
                                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                                            <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center">
                                                <UserGroupIcon className="size-7 text-indigo-400" />
                                            </div>
                                            <div className="text-center">
                                                <p className="text-sm font-semibold text-gray-800">Aucun collaborateur pour l'instant</p>
                                                <p className="text-xs text-gray-500 mt-1">Ajoutez une secrétaire ou un assistant pour partager l'accès.</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setDrawerOpen(true)}
                                                className="btn-primary flex items-center gap-2"
                                            >
                                                <UserPlusIcon className="size-4" />
                                                Ajouter un membre
                                            </button>
                                        </div>
                                    ) : (
                                        /* ── Liste des membres ── */
                                        <>
                                            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 mb-4">
                                                {nonNotaires.map((c) => (
                                                    <li key={c.id} className="flex items-center justify-between px-4 py-3">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{c.prenom} {c.nom}</p>
                                                            <p className="text-xs text-gray-500">{c.email}</p>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-600">
                                                                Secrétaire
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDeleteModal({ id: c.id, prenom: c.prenom, nom: c.nom })}
                                                                disabled={deletingId === c.id}
                                                                className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                                                title="Supprimer l'accès"
                                                            >
                                                                <TrashIcon className="size-4" />
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                            {limitAtteinte && (
                                                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                                    Limite de {MAX_COLLABORATEURS} collaborateurs atteinte.
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── Abonnement ── */}
                        {activeTab === "abonnement" && (
                            <div className="overflow-hidden rounded-lg bg-white">
                                <div className="px-4 py-3 sm:p-6">
                                    <TabsProfilFacture user={context.user} />
                                </div>
                            </div>
                        )}

                        {/* ── Mot de passe ── */}
                        {activeTab === "mdp" && (
                            <div className="overflow-hidden rounded-lg bg-white">
                                <div className="px-4 py-3 sm:p-6">
                                    <h3 className="text-base font-semibold leading-7 text-gray-900 mb-1">Modifier le mot de passe</h3>
                                    <Divider className="mb-6" />
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">Ancien mot de passe</label>
                                            <Input type="password" value={oldPassword} onChange={(val) => setOldPassword(val.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">Nouveau mot de passe</label>
                                            <Input type="password" value={newPassword} onChange={(val) => setNewPassword(val.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium leading-6 text-gray-900">Confirmer le nouveau mot de passe</label>
                                            <Input type="password" value={newPasswordConfirm} onChange={(val) => setNewPasswordConfirm(val.target.value)} />
                                        </div>
                                        <div>
                                            <button type="button" className="btn-primary" onClick={updatePassword}>Modifier</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Modal suppression ── */}
            <Dialog open={!!deleteModal} onClose={() => setDeleteModal(null)} className="relative z-50">
                <DialogBackdrop className="fixed inset-0 bg-black/30" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                                <ExclamationTriangleIcon className="size-5 text-red-500" />
                            </div>
                            <div>
                                <DialogTitle className="text-sm font-semibold text-gray-900">
                                    Supprimer l'accès
                                </DialogTitle>
                                <p className="mt-1 text-sm text-gray-500">
                                    Êtes-vous sûr(e) de vouloir supprimer l'accès de <strong>{deleteModal?.prenom} {deleteModal?.nom}</strong> ? Cette action est irréversible.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setDeleteModal(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                onClick={handleRemoveCollaborateur}
                                disabled={!!deletingId}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {deletingId ? "Suppression..." : "Supprimer"}
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>

            {/* ── Drawer invitation ── */}
            {drawerOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/30" onClick={() => setDrawerOpen(false)} />
                    {/* Panel */}
                    <div className="relative w-full max-w-md bg-white shadow-xl flex flex-col h-full">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h2 className="text-base font-semibold text-gray-900">Ajouter un collaborateur</h2>
                            <button type="button" onClick={() => setDrawerOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="size-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <p className="text-sm text-gray-500 mb-6">
                                Un email avec les identifiants de connexion sera envoyé automatiquement.
                            </p>
                            <form id="invite-form" onSubmit={handleInviteCollaborateur} className="flex flex-col gap-2">
                                <Field>
                                    <Label>Prénom</Label>
                                    <Input type="text" value={invitePrenom} onChange={(e) => setInvitePrenom(e.target.value)} required />
                                </Field>
                                <Field>
                                    <Label>Nom</Label>
                                    <Input type="text" value={inviteNom} onChange={(e) => setInviteNom(e.target.value)} required />
                                </Field>
                                <Field>
                                    <Label>Email</Label>
                                    <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
                                </Field>
                            </form>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200">
                            <button
                                type="submit"
                                form="invite-form"
                                className="btn-primary w-full flex items-center justify-center gap-2"
                                disabled={inviteLoading}
                            >
                                {inviteLoading ? "Envoi en cours..." : "Envoyer l'invitation"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Profil;
